import { useEffect, useState } from "react";
import SharedCTASection from "../../SharedCTASection";
import PricingNavContentPage2 from "./PricingNavContentPage2";
import PricingNavContentPageCompare from "./PricingNavContentPageCompare";

const plans = [
  {
    name: "base",
    price: "$18",
    period: "/ month",
    desc: "get the essentials",
    nameColor: "#D1BCFF",
    accentColor: "#7000FF",
    dotColor: "#D1BCFF",
    mostPopular: false,
    features: [
      "free 30 day trial",
      "geo online presence (1)",
      "digital menus or catalogs (1)",
      "unlimited chat ordering",
      "free ai ordering support (1k msgs)",
    ],
  },
  {
    name: "premium",
    price: "$80",
    period: "/ month",
    desc: "everything in base just more…",
    nameColor: "#FFB599",
    accentColor: "#FFB599",
    dotColor: "#FFB599",
    badgeBg: "#FFB599",
    mostPopular: true,
    features: [
      "free 30 day trial",
      "geo online presence (3)",
      "digital menus or catalogs (10)",
      "free ai ordering support (40k msgs)",
      "team members (20)",
    ],
  },
  {
    name: "premium plus",
    price: "$230",
    period: "/ month",
    desc: "everything in premium just way more…",
    nameColor: "#FACC15",
    accentColor: "#FACC15",
    dotColor: "#FACC15",
    badgeBg: null,
    mostPopular: false,
    features: [
      "free 30 day trial",
      "geo online presence (6)",
      "digital menus or catalogs (20)",
      "free ai ordering support (100k msgs)",
      "team members (60)",
    ],
  },
  {
    name: "ultimate",
    price: "$400",
    period: "/ month",
    desc: "everything you need…",
    nameColor: "#34D399",
    accentColor: "#34D399",
    dotColor: "#34D399",
    mostPopular: false,
    features: [
      "free 30 day trial",
      "geo online presence (10)",
      "digital menus or catalogs (20)",
      "free ai ordering support (200k msgs)",
      "team members (150)",
    ],
  },
];

const CheckIcon = ({ color }) => (
  <svg width="12.25" height="12.25" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="10" cy="10" r="10" fill={color} fillOpacity="0.25" />
    <path d="M6 10.5l2.5 2.5 5.5-5.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function PricingNavContentPage1(props) {
  const [btnValue, setBtnValue] = useState("base");
  const [compare, setCompareBtn] = useState(false);

  useEffect(() => {
    if (props.defaultCompare) setCompareBtn(false);
  }, [props.defaultCompare]);

  return (
    <>
      <div style={{ backgroundColor: "#020013" }}>
        {/* Cards */}
        <section className="w-full pb-12 md:px-12 lg:px-20 xl:px-28">
          {/* Mobile: horizontal scroll | Desktop: grid */}
          <div className="
            flex flex-nowrap overflow-x-auto gap-4 px-4 pb-4 pt-5
            snap-x snap-mandatory
            md:grid md:grid-cols-2 md:overflow-visible
            xl:grid-cols-4
            max-w-7xl mx-auto md:px-0 md:pb-0 md:pt-0
            [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
          ">
            {plans.map((plan, index) => (
              <div key={index} className="relative flex flex-col flex-shrink-0 w-[calc(100vw-32px)] md:w-auto snap-start">

                {/* Most popular badge — sits on top border */}
                {plan.mostPopular && (
                  <div className="absolute -top-[14px] left-1/2 -translate-x-1/2 z-10">
                    <span
                      className="inline-flex items-center lowercase"
                      style={{
                        backgroundColor: plan.badgeBg,
                        color: "#5A1C00",
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 500,
                        fontSize: "13px",
                        padding: "4px 12px",
                        borderRadius: "9999px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      most popular
                    </span>
                  </div>
                )}

                {/* Card */}
                <div
                  className="flex flex-col flex-grow"
                  style={{
                    border: "1px solid rgba(112,0,255,0.2)",
                    borderRadius: "32px",
                    overflow: "hidden",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                  }}
                >
                  {/* Accent top bar — straight line clipped by parent border-radius */}
                  <div style={{ height: "4px", backgroundColor: plan.accentColor, flexShrink: 0 }} />

                  {/* Inner content */}
                  <div className="flex flex-col flex-grow" style={{ backgroundColor: "rgba(26,26,26,0.8)", padding: "32px" }}
                >
                  {/* Plan name */}
                  <p
                    className="lowercase mb-3"
                    style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: "16px", lineHeight: "24px", letterSpacing: "1.6px", color: plan.nameColor }}
                  >
                    {plan.name}
                  </p>

                  {/* Price */}
                  <div className="flex items-baseline gap-1 mb-1">
                    <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: "48px", lineHeight: "1", color: "#FFFFFF" }}>
                      {plan.price}
                    </span>
                    <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "14px", color: "rgba(255,255,255,0.5)" }}>
                      {plan.period}
                    </span>
                  </div>

                  {/* Desc */}
                  <p
                    className="lowercase mb-5"
                    style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "14px", color: "rgba(255,255,255,0.5)" }}
                  >
                    {plan.desc}
                  </p>

                  {/* Features */}
                  <ul className="flex flex-col gap-3 flex-grow mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 lowercase">
                        <CheckIcon color={plan.dotColor} />
                        <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 400, fontSize: "16px", lineHeight: "24px", letterSpacing: "0px", color: "#E7DFF0" }}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Buttons */}
                  <div className="flex flex-col gap-3 mt-auto">
                    <button
                      onClick={() => window.location.href = "/business/get-started"}
                      className="w-full lowercase cursor-pointer transition-opacity hover:opacity-80"
                      style={{ height: "48px", borderRadius: "9999px", backgroundColor: "#6E3FC6", color: "#FFFFFF", fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: "15px", border: "none", textAlign: "center" }}
                    >
                      free trial
                    </button>
                    <button
                      onClick={() => window.location.href = "/business/demo"}
                      className="w-full lowercase cursor-pointer transition-opacity hover:opacity-80"
                      style={{ height: "48px", borderRadius: "9999px", backgroundColor: "transparent", color: "#FFFFFF", fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: "15px", border: "1px solid rgba(255,255,255,0.2)", textAlign: "center" }}
                    >
                      book a demo
                    </button>
                  </div>
                  </div>{/* end inner content */}
                </div>{/* end card */}
              </div>
            ))}
          </div>
        </section>

        {/* Compare all features */}
        <div className="flex items-center justify-center pb-6 md:pb-16">
          <button
            className="lowercase cursor-pointer transition-opacity hover:opacity-80"
            style={{ height: "48px", paddingLeft: "32px", paddingRight: "32px", borderRadius: "9999px", backgroundColor: "transparent", color: "#FFFFFF", fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: "14px", border: "1px solid rgba(255,255,255,0.2)" }}
            onClick={() => setCompareBtn(!compare)}
          >
            {compare ? "hide comparison" : "compare all features"}
          </button>
        </div>

        {compare && <PricingNavContentPageCompare />}
        {!compare && <PricingNavContentPage2 btnContentValue={btnValue} />}
      </div>
      <SharedCTASection />
    </>
  );
}

export default PricingNavContentPage1;
